import { useMemo } from 'react';
import { Autocomplete } from 'src/components/Autocomplete';
import { Chip } from 'src/components/Chip';
import { TextField } from 'src/components/TextField';
import { useOptionalSession } from 'src/contexts/SessionContext';

interface ServiceCategoryFilterProps {
  field: string;
  label: string;
  value: string | string[] | undefined;
  setValue: (newVal: string | string[]) => void;
  valueOnly?: boolean;
  multiple?: boolean;
}

export const ServiceCategoryFilter: React.FC<ServiceCategoryFilterProps> = ({
  label,
  value,
  setValue,
  valueOnly,
  multiple = true,
}) => {
  // Normalize value to always be an array for internal use
  const selectedValues = Array.isArray(value)
    ? value
    : value
      ? [value]
      : [];

  const { session } = useOptionalSession();

  const options = useMemo(
    () => session?.serviceTags || [],
    [session?.serviceTags]
  );

  if (valueOnly) {
    return selectedValues.length > 0 ? <>{selectedValues.join(', ')}</> : null;
  }

  if (multiple) {
    return (
      <Autocomplete
        multiple
        options={options}
        getOptionLabel={(option) => option}
        value={selectedValues}
        onChange={(_, newValue) => setValue(newValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={!selectedValues.length ? 'Any' : ''}
          />
        )}
        renderTags={(value: readonly string[], getTagProps) =>
          value.map((option: string, index: number) => {
            const { key, ...tagProps } = getTagProps({ index });
            return <Chip label={option} key={key} {...tagProps} />;
          })
        }
      />
    );
  }

  // Single selection mode
  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option) => option}
      value={selectedValues[0] || null}
      onChange={(_, newValue) => setValue(newValue || '')}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={!selectedValues.length ? 'Any' : ''}
        />
      )}
    />
  );
};
